import { logger } from './logger';
import { withRetry } from './error-handling';

/**
 * Centralized API helpers
 * Eliminates duplicate fetch patterns across the codebase
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface FetchOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  params?: Record<string, string | number | boolean | undefined | null>;
}

export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  status: number;
}

// ============================================================================
// FETCH UTILITIES
// ============================================================================

/**
 * Enhanced fetch with timeout support
 */
export async function fetchWithTimeout(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { timeout = 30000, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }

    throw error;
  }
}

/**
 * Fetch with automatic retry on failure
 */
export async function fetchWithRetry(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { retries = 3, retryDelay = 1000, ...fetchOptions } = options;

  return withRetry(() => fetchWithTimeout(url, fetchOptions), {
    maxAttempts: retries,
    delayMs: retryDelay,
    shouldRetry: (error, attempt) => {
      // Don't retry on 4xx errors (client errors)
      if (
        error instanceof Response &&
        error.status >= 400 &&
        error.status < 500
      ) {
        return false;
      }

      logger.debug(
        `Retrying request to ${url} (attempt ${attempt}/${retries})`
      );
      return true;
    }
  });
}

/**
 * JSON API request helper
 * Handles JSON serialization/deserialization and error responses
 */
export async function apiRequest<T = unknown>(
  url: string,
  options: FetchOptions = {}
): Promise<ApiResponse<T>> {
  try {
    // Handle query parameters
    let finalUrl = url;
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(
        ([key, value]: [
          string,
          string | number | boolean | undefined | null
        ]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
      );
      const queryString = searchParams.toString();
      if (queryString) {
        finalUrl = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
      }
    }

    const { params: _params, ...fetchOptions } = options;

    const response = await fetchWithRetry(finalUrl, {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      }
    });

    // Handle non-2xx responses
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If response is not JSON, try text
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch {
          // Use default error message
        }
      }

      return {
        error: errorMessage,
        status: response.status
      };
    }

    // Handle empty responses
    if (
      response.status === 204 ||
      response.headers.get('content-length') === '0'
    ) {
      return {
        data: undefined,
        status: response.status
      };
    }

    // Parse JSON response
    const data = await response.json();

    return {
      data,
      status: response.status
    };
  } catch (error) {
    logger.error(`API request to ${url} failed:`, error);
    return {
      error: error instanceof Error ? error.message : 'Network error occurred',
      status: 0
    };
  }
}

// ============================================================================
// SPECIALIZED API HELPERS
// ============================================================================

/**
 * GET request helper
 */
export async function apiGet<T = unknown>(
  url: string,
  options?: Omit<FetchOptions, 'method' | 'body'>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    ...options,
    method: 'GET'
  });
}

/**
 * POST request helper
 */
export async function apiPost<T = unknown>(
  url: string,
  data?: unknown,
  options?: Omit<FetchOptions, 'method' | 'body'>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  });
}

/**
 * PUT request helper
 */
export async function apiPut<T = unknown>(
  url: string,
  data?: unknown,
  options?: Omit<FetchOptions, 'method' | 'body'>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  });
}

/**
 * Presigned URL upload helper
 * Handles direct uploads to presigned URLs (e.g., S3)
 */
export async function uploadToPresignedUrl(
  presignedUrl: string,
  file: File,
  options?: FetchOptions
): Promise<Response> {
  return fetchWithRetry(presignedUrl, {
    ...options,
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
      ...options?.headers
    }
  });
}

// ============================================================================
// RESPONSE HELPERS
// ============================================================================

/**
 * Extract error message from various response formats
 */
export function extractErrorMessage(response: ApiResponse): string {
  if (response.error) {
    return response.error;
  }

  if (response.data && typeof response.data === 'object') {
    // Check common error field names
    const errorFields = ['error', 'message', 'error_message', 'errorMessage'];
    const data = response.data as Record<string, unknown>;

    for (const field of errorFields) {
      if (data[field]) {
        return String(data[field]);
      }
    }
  }

  return `Request failed with status ${response.status}`;
}

/**
 * Type guard for successful response
 */
export function isSuccessResponse<T>(
  response: ApiResponse<T>
): response is { data: T; status: number } {
  return !response.error && response.data !== undefined;
}
